<template>
  <HomeContentLayout>
    <template v-slot:main-content>
      <div class="flex space-x-5 items-center">
        <contenteditable tag="h3" id="contentedit" class="text-lg font-semibold p-3" :contenteditable="editTitle"
          v-model="document.title" :no-nl="true" :no-html="true" @returned="updateTitle" @blur="updateTitle" />

        <Icon @click="handleEditTitle" icon="ant-design:edit" class="cursor-pointer text-xl ml-2" />
        <span id="txtsave" class="text-black/40 text-sm">Saved</span>
        <div class="!ml-auto !mb-5 !mt-5">
          <v-btn v-if="isRefresh" @click="onSelectedModuleChange()" variant="flat"
            class="!py-3 !bg-primary-light !rounded-xl !mr-4 !tracking-normal !capitalize !text-black !h-auto"
            prepend-icon="mdi-reload">
            Refresh
            <v-tooltip v-if="selectedModule === 'grammar-check-module'" activator="parent" location="top">Update the
              results you see by clicking on this button.</v-tooltip>
          </v-btn>
          <v-btn @click="downloadDocument()" variant="flat"
            class="!py-3 !bg-primary-main !rounded-xl !tracking-normal !capitalize !text-white !h-auto"
            prepend-icon="mdi-cloud-download-outline">
            Download Document
          </v-btn>
        </div>
      </div>
      <div>
        <div id="quillContainer" class="disable-languageTool text-editor-box-step editor-container" style="border: 0px">
          <div :class="{
            'p-2 border-4 border-red-500 rounded-lg': currentStep === 'text-editor-box'
          }">
            <quill-editor ref="theEditor" v-model:content="query" contentType="text" theme=""
              :enable="!getLoadingBrainResponse" :readOnly="getLoadingBrainResponse" v-show="!getModuleDrawerLoading"
              :options="editorOptions" @ready="onEditorReady" v-on:selection-change="handleSelectionChange"
              v-on:text-change="handleTextChange" style="color: #191818 !important; line-height: 1.5"
              class="text-editor"></quill-editor>
          </div>
        </div>
      </div>
    </template>

    <template v-slot:secondary-content>
      <AITextGenerator v-if="getSelectedModule === 'ai-text-generation-module'" @insertSentence="handleInsertSentence"
        :tour="tour">
      </AITextGenerator>
      <DraftGenie v-else-if="getSelectedModule === 'draft-genie-module'" @insertSentence="handleInsertSentence"
        :tour="tour">
      </DraftGenie>
      <CoverLetter v-else-if="getSelectedModule === 'cover-letter-module'" @insertSentence="handleInsertSentence"
        :tour="tour">
      </CoverLetter>
      <JournalSelector v-else-if="getSelectedModule === 'journal-selector-module'"
        @insertSentence="handleInsertSentence">
      </JournalSelector>
      <Reviewer v-else-if="getSelectedModule === 'reviewer-module'" @insertSentence="handleInsertSentence">
      </Reviewer>
      <GrammarCheck v-else-if="getSelectedModule === 'grammar-check-module'"> </GrammarCheck>
      <ParagraphLength v-else-if="getSelectedModule === 'paragraph-length-module'"
        :paragraphLengthFeedbackData="getParagraphLengthFeedbackData" :highlightedCard="getHighlightedCard"
        @selectCardSentence="selectCardSentence" :quillEditor="getQuill">
      </ParagraphLength>
      <Paraphraser v-else-if="getSelectedModule === 'paraphraser-module'" :selectedText="getSelectedText"
        :paraphrasedText="getParaphrasedText" @insertSentence="handleInsertSentence"
        @paraphraseText="handleParaphraserModuleSelection" @tryAgain="tryAgain">
      </Paraphraser>
      <PlagiarismChecker v-else-if="getSelectedModule === 'plagiarism-checker-module'"
        :totalPlagiarism="getTotalPlagiarism" :plagiarismFeedbackData="getPlagiarismFeedbackData"
        :highlightedCard="getHighlightedCard" @selectCardSentence="selectCardSentence" :quillEditor="getQuill">
      </PlagiarismChecker>
      <SentenceLength v-else-if="getSelectedModule === 'sentence-length-module'"
        :sentenceLengthFeedbackData="getSentenceLengthFeedbackData" :highlightedCard="getHighlightedCard"
        @selectCardSentence="selectCardSentence" :quillEditor="getQuill">
      </SentenceLength>
      <ReferenceSearch v-else-if="getSelectedModule === 'reference-search-module'"
        @insertSentence="handleInsertSentence" :tour="tour">
      </ReferenceSearch>
      <SentenceTemplates v-else-if="getSelectedModule === 'sentence-templates-module'"
        :sentenceTemplateFeedbackData="getSentenceTemplateFeedbackData" @insertSentence="handleInsertSentence">
      </SentenceTemplates>
      <Summarizer v-else-if="getSelectedModule === 'summarizer-module'" :selectedText="getSelectedText"
        :summarizedText="getSummarizedText" @insertSentence="handleInsertSentence"
        @summarizeText="handleSummarizerModuleSelection" @tryAgain="tryAgain">
      </Summarizer>
      <Transitions v-else-if="getSelectedModule === 'transition-module'"
        :transitionFeedbackData="getTransitionFeedbackData" :transitionSuggestions="getTransitionSuggestions"
        :highlightedCard="getHighlightedCard" @insertSentence="handleInsertTransitionWord"
        @selectCardSentence="selectCardSentence" :quillEditor="getQuill">
      </Transitions>
      <div v-else>Select one of the functions to get started</div>
    </template>

  </HomeContentLayout>

  <Featured v-model="isFeaturedFunction" />
</template>

<script lang="ts">
import { Icon } from '@iconify/vue';
import HomeContentLayout from '@/layouts/HomeContentLayout.vue';
import AITextGenerator from '@/views/home/functions/AITextGenerator.vue';
import DraftGenie from '@/views/home/functions/DraftGenie.vue';
import Reviewer from '@/views/home/functions/Reviewer.vue';
import JournalSelector from '@/views/home/functions/JournalSelector.vue';
import CoverLetter from '@/views/home/functions/CoverLetter.vue';
import GrammarCheck from '@/views/home/functions/GrammarCheck.vue';
import ParagraphLength from '@/views/home/functions/ParagraphLength.vue';
import Paraphraser from '@/views/home/functions/Paraphraser.vue';
import PlagiarismChecker from '@/views/home/functions/PlagiarismChecker.vue';
import SentenceLength from '@/views/home/functions/SentenceLength.vue';
import SentenceTemplates from '@/views/home/functions/SentenceTemplates.vue';
import Summarizer from '@/views/home/functions/Summarizer.vue';
import Transitions from '@/views/home/functions/Transitions.vue';
import ReferenceSearch from '@/views/home/functions/ReferenceSearch.vue';
import Featured from '@/components/global/Featured.vue';
import { updateDocument, fetchDocument, downloadDocument, trackSession } from '@/actions/document';
import { usePayloadStore } from '@/stores/payload';
import { Vue, Options } from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import { QuillEditor, Quill } from '@vueup/vue-quill';
import {
  sentenceLength,
  paragraphLength,
  transition,
  transitConnectors,
  sentenceTemplates,
  v2SummarizeText,
  v2ParaphraseText,
  plagiarismChecker
} from '@/actions/functions';
import registerQuillLanguageTool from 'quill-languagetool2';
import * as Grammarly from '@grammarly/editor-sdk';
import $ from 'jquery';
import contenteditable from 'vue-contenteditable';
import Message from 'antd-message';
import { saveAs } from 'file-saver';
import 'quill/dist/quill.core.css';
import * as diff from 'diff';
import IdleJs from 'idle-js';
import moment from 'moment';
import { offset } from '@floating-ui/dom';
import { socket } from "../../socket";

registerQuillLanguageTool(Quill);

// Patch the quill-languagetool2 module to fix the SuggestionBlot.create issue
const originalCreateSuggestionBlot = Quill.import('formats/ltmatch');
if (originalCreateSuggestionBlot) {
  // Fix the create method by adding a null check
  originalCreateSuggestionBlot.create = function(match: { offset: { toString: () => any; } | undefined; length: { toString: () => any; } | undefined; rule: { id: any; }; }) {
    const node = Object.getPrototypeOf(originalCreateSuggestionBlot).create.call(this);
    
    // Only add attributes if match is defined
    if (match && match.offset !== undefined && match.length !== undefined && match.rule) {
      node.setAttribute("data-offset", match.offset.toString());
      node.setAttribute("data-length", match.length.toString());
      node.setAttribute("data-rule-id", match.rule.id);
    }
    
    return node;
  };
}

// Add event listener to handle null rule IDs at the document level
document.addEventListener('click', (e: Event) => {
  try {
    const target = e.target as HTMLElement;
    if (target && 
        target.classList && 
        target.classList.contains('lt-popup-suggestion') && 
        !target.getAttribute('data-rule-id')) {
      e.preventDefault();
      e.stopPropagation();
    }
  } catch (err) {
    console.error('Error in popup click handler:', err);
  }
}, true);

let toolbarOptions = [
  [{ header: [false, 1, 2, 3] }],
  [{ font: [] }],
  //[{ size: ["small", false, "large", "huge"] }], // custom dropdown
  [], //["bold", "italic", "underline"], //, "strike"], // toggled buttons
  [], //[{ color: [] }, { background: [] }], // dropdown with defaults from theme
  [{ align: [] }],
  [{ list: 'ordered' }, { list: 'bullet' }]
  //[{ indent: "-1" }, { indent: "+1" }], // outdent/indent
  //["blockquote", "code-block"],
  //[{ header: 1 }, { header: 2 }], // custom button values
  //[{ script: "sub" }, { script: "super" }], // superscript/subscript
  //[{ direction: "rtl" }], // text direction
  //["clean"] // remove formatting button
];

@Options({
  components: {
    QuillEditor,
    HomeContentLayout,
    AITextGenerator,
    DraftGenie,
    Reviewer,
    JournalSelector,
    CoverLetter,
    GrammarCheck,
    ParagraphLength,
    Paraphraser,
    PlagiarismChecker,
    SentenceLength,
    SentenceTemplates,
    Summarizer,
    Transitions,
    ReferenceSearch,
    Icon,
    contenteditable,
    Featured
  }
})
export default class Functions extends Vue {
  private payloadStore = usePayloadStore();

  public selectedModule: string = '';

  public editTitle: boolean = false;

  public selectedText: string = '';

  public tour: any = null;

  public currentStep: string = '';

  public isFeaturedFunction = false;
  public openFeaturedFunctions() {
    if (!this.payloadStore.payload.displayedFeatured) {
      this.isFeaturedFunction = true;
    }
    this.payloadStore.change({ displayedFeatured: true });
  }

  public initOnboarding() {
    this.$nextTick(() => {
      let thiz: any = this;

      const tour = thiz.$shepherd({
        tourName: 'Onboarding',
        useModalOverlay: true,
        confirmCancel: true,
        keyboardNavigation: false,
        defaultStepOptions: {
          cancelIcon: {
            enabled: false
          },
          beforeShowPromise: () =>
            new Promise((resolve) => {
              const currentIndex = tour.steps.indexOf(tour.getCurrentStep());
              let currentStep = '';
              setTimeout(() => {
                let currentStep: any = tour.steps[currentIndex].el;
                if (currentStep.hasAttribute('data-popper-reference-hidden')) {
                  const addCSS = (css: any) =>
                    (document.head.appendChild(document.createElement('style')).innerHTML = css);
                  addCSS(`.shepherd-element[data-popper-reference-hidden]:not(.shepherd-centered) {
                      opacity: initial;
                      pointer-events: initial;
                      visibility: initial;
                    }`);
                }
              }, 100);
              resolve(0);
            }),
          scrollTo: false,
          classes: 'shepherd-theme-custom',
          modalOverlayOpeningPadding: 14,
          modalOverlayOpeningRadius: 16,
          when: {
            show() {
              const currentStep = tour.getCurrentStep();
              const currentStepCount = tour.steps.indexOf(currentStep) + 1;
              const currentStepElement = currentStep.getElement();
              const steps = 3;
              let stepsHTML = '';

              for (let i = 1; i <= steps; i++) {
                const stepSymbol = currentStepCount > i ? '✓' : i;
                const stepClass =
                  currentStepCount >= i
                    ? ' border-primary-main bg-primary-main text-white'
                    : ' border-[#00000040] bg-app-gray text-[#00000040]';
                stepsHTML += `
                <li>
                  <label for="step${i}" class="w-8 h-8 font-normal rounded-full border flex items-center justify-center text-sm cursor-default ${stepClass}">${stepSymbol}</label>
                </li>`;
                if (i !== steps) {
                  stepsHTML += `
                  <li class="border-t border-[#00000040] flex-grow h-0"></li>`;
                }
              }

              const header = `
              <div class="bg-app-gray w-full p-4 rounded-t-2xl">
                <ul class="flex items-center space-x-2 w-56 pl-3">
                  <li class="text-lg font-medium">Steps:</li>
                  ${stepsHTML}
                </ul>
              </div>`;

              currentStepElement
                .querySelector('.shepherd-text')
                .insertAdjacentHTML('beforebegin', header);

              currentStepElement
                .querySelector('.shepherd-footer')
                .insertAdjacentHTML('beforebegin', `<hr class="pb-2" />`);
            }
          }
        }
      });

      ['complete', 'close', 'cancel'].forEach((event) =>
        tour.on(event, () => {
          this.payloadStore.change({
            tour: { ...this.payloadStore.payload?.tour, currentStep: '' }
          });
          localStorage.setItem('tour-status', 'inactive');
        })
      );

      tour.on('show', (event: any) => {
        if (this.tour?.isActive()) {
          this.payloadStore.change({
            tour: { ...this.payloadStore.payload?.tour, currentStep: event?.step?.id }
          });
        } else {
          this.payloadStore.change({
            tour: { ...this.payloadStore.payload?.tour, currentStep: '' }
          });
        }
      });

      let steps = [
        {
          text: `
          <div class="text-start p-4">
            <h1 class="font-bold text-base mb-4">Write your prompt or instruction</h1>
            <h3 class="text-sm 2xl:text-base leading-6">
              Write your instructions clearly in the text box and then press the "Generate text" button.
            </h3>
          </div>`,
          attachTo: {
            element: '.text-generator-input-box-step',
            on: 'left-start'
          },
          id: 'text-generator-input-box',
          floatingUIOptions: {
            middleware: [
              offset({
                mainAxis: 35,
                crossAxis: -11
              })
            ]
          },
          buttons: [
            {
              action: () => {
                return this.payloadStore.change({
                  tour: { ...this.payloadStore.payload?.tour, runAITextGeneratorGenerateText: true }
                });
              },
              text: `Next`
            }
          ]
        },
        {
          text: `
          <div class="text-start p-4">
            <h1 class="font-bold text-base mb-4">Insert the response in the text editor</h1>
            <h3 class="text-sm 2xl:text-base leading-6">
              Read the response created by WriteWise and paste it into the text editor.
            </h3>
          </div>`,
          attachTo: {
            element: '.text-generator-chat-box-step',
            on: 'left'
          },
          id: 'text-generator-chat-box',
          floatingUIOptions: {
            middleware: [
              offset({
                mainAxis: 35,
                crossAxis: -30
              })
            ]
          },
          buttons: [
            {
              action: () => {
                return this.payloadStore.change({
                  tour: { ...this.payloadStore.payload?.tour, runAITextGeneratorInsertText: true }
                });
              },
              text: `Next`
            }
          ]
        },
        {
          text: `
          <div class="text-start p-4">
            <h1 class="font-bold text-base mb-4">Edit and refine the text</h1>
            <h3 class="text-sm 2xl:text-base leading-6">
              Add your finishing touches to the generated content. Adjust the tone, style, and details to make it perfectly suited to your message.
            </h3>
          </div>`,
          attachTo: {
            element: '.text-editor-box-step',
            on: 'bottom'
          },
          id: 'text-editor-box',
          floatingUIOptions: {
            middleware: [
              offset({
                mainAxis: 35
              })
            ]
          },
          buttons: [
            {
              action: function (): any {
                const thiz: any = this;
                return thiz.complete();
              },
              text: `Finish`
            }
          ]
        }
      ];
      tour.addSteps(steps);
      this.tour = tour;
      tour.start();
    });
  }

  public onEditorReady(quill: any) {
    this.quill = quill;
    this.quill.clipboard.addMatcher(Node.ELEMENT_NODE, (node: any, delta: any) => {
      let ops: any[] = [];
      delta.ops.forEach((op: any) => {
        if (op.insert && typeof op.insert === 'string') {
          ops.push({
            insert: op.insert
          });
        }
      });
      delta.ops = ops;
      return delta;
    });

    var Parchment = Quill.import('parchment');
    let Inline = Quill.import('blots/inline');
    class SentenceClass extends Inline {
      static create(value: any) {
        let node = super.create();
        node.classList.add('ww-sentence-' + value.type);
        node.classList.add('ww-sentence-id' + value.id);
        if (value.ondblclick) {
          node.addEventListener('dblclick', () => value.ondblclick(node));
        }
        if (value.onmouseover) {
          node.addEventListener('mouseover', () => value.onmouseover(node));
        }
        if (value.onmouseout) {
          node.addEventListener('mouseout', () => value.onmouseout(node));
        }
        return node;
      }
      static formats(node: any) {
        return { title: node.getAttribute('title') };
      }
    }
    SentenceClass.blotName = 'ww-sentence';
    SentenceClass.tagName = 'span';
    Quill.register(SentenceClass, true);

    let SimilarityClass = new Parchment.Attributor.Class('similarity', 'ww-similarity', {
      scope: Parchment.Scope.INLINE
    });
    Quill.register(SimilarityClass, true);
    document.addEventListener('click', this.handleClickOutside);
    //     this.quill.on('quill:selection', function(range) {
    //       console.log(range, 'dsddssd')
    //   const nativeRange = quill.getSelection();
    //   if (nativeRange) {
    //     console.log(nativeRange, 'sdssd');
    //   }
    // });
    setTimeout(() => {
      if (!this.loadingResponse) this.onSelectedModuleChange();
    }, 5000);

    this.quillLoaded = true;
  }

  public loadingResponse: any = this.payloadStore.payload.loadingResponse;

  @Watch('payloadStore.payload.loadingResponse')
  public onLoadingResponse() {
    this.loadingResponse = this.payloadStore.payload.loadingResponse;
  }

  public loadingBrainResponse: any = this.payloadStore.payload.loadingBrainResponse;
  @Watch('payloadStore.payload.loadingBrainResponse')
  public onloadingBrainResponse() {
    this.loadingBrainResponse = this.payloadStore.payload.loadingBrainResponse;
  }

  get getLoadingBrainResponse() {
    return this.loadingBrainResponse;
  }

  public downloadDocument() {
    this.payloadStore.change({ loadingBrainResponse: true });
    downloadDocument(this.payloadStore.payload.user.pk, this.query)
      .then((res: any) => {
        saveAs(res.data, `${this.document.title}.docx`);
        Message.success('Document downloaded successfully');
        this.payloadStore.change({ loadingBrainResponse: false });
      })
      .catch((err) => {
        console.log(err);
        Message.error('Could not download document');
        this.payloadStore.change({ loadingBrainResponse: false });
      });
  }

  public handleEditTitle() {
    this.editTitle = !this.editTitle;
    const edList = document.getElementById('contentedit');
    setTimeout(function () {
      (edList as any).focus();
    }, 0);
  }

  public async updateTitle() {
    await this.updateDocument();
    this.editTitle = false;
  }

  public document: any = {};

  public async getDocument() {
    try {
      this.payloadStore.change({ loadingResponse: true });
      const res = await fetchDocument(this.payloadStore.payload.document.id);
      this.document = res;
      this.openFeaturedFunctions();
      await this.payloadStore.updateInitialText(res.data);
      this.payloadStore.change({ loadingResponse: false });
      return res;
    } catch (err) {
      Message.error('Could not fetch document. Please reload your browser.');
      this.payloadStore.change({ functionError: true });
      this.payloadStore.change({ loadingResponse: false });
    }
  }

  get getQuill() {
    return this.quill;
  }

  public initialText = '';

  public async updateDocument() {
    await this.queueSave();
  }

  public whenIdle() {
    console.log('whenIdle');
    this.payloadStore.change({ idle_date: moment().format() });
    // this.trackSession({ session_type: 'Idle' });
  }

  public whenActive() {
    console.log('whenActive');
    this.payloadStore.change({ active_date: moment().format() });
    // this.trackSession({ session_type: 'Active' });
  }

  public async trackSession({ session_type }: any) {
    const active_date = this.payloadStore.payload.active_date;
    const idle_date = this.payloadStore.payload.idle_date;
    if (active_date && idle_date && !!this.getSelectedModule && this.document.id) {
      let active_duration = 0;
      let inactive_duration = 0;
      if (session_type == 'Idle') {
        active_duration = moment(idle_date).diff(moment(active_date), 'minutes');
        this.payloadStore.change({ active_date: '' });
      } else {
        inactive_duration = moment(active_date).diff(moment(idle_date), 'minutes');
        this.payloadStore.change({ idle_date: '' });
      }

      const { modified, deletions, count } = await this.fetchAdditionDeletion();
      this.saveSession({
        modified,
        deletions,
        count,
        active_duration,
        inactive_duration
      });
    }
  }

  public async fetchAdditionDeletion() {
    let deletions = 0;
    let modified = 0;
    let currentText = this.quill.getText().trim();
    let initialText = (await this.payloadStore.getInitialText() as any).trim();
    const currentTextCount = currentText ? currentText.split(' ').length : 0;
    const initialTextCount = initialText ? initialText.split(' ').length : 0;
    let added = currentTextCount - initialTextCount;
    added = added >= 0 ? added : 0;
    const wordDiff = diff.diffWords(initialText, currentText);
    for (const diff of wordDiff) {
      if (diff.added && diff.value) {
        modified += diff.value.trim().split(' ').length;
      }
      if (diff.removed && diff.value) {
        deletions += diff.value.trim().split(' ').length;
      }
    }
    modified = modified - added;
    modified = modified < 0 ? 0 : modified;
    return {
      modified,
      deletions,
      count: added,
      active_duration: 0,
      inactive_duration: 0
    };
  }

  public async saveSessionOnUpdate() {
    const data = await this.fetchAdditionDeletion();
    this.saveSession(data);
  }

  @Watch('payloadStore.payload.selectedModule')
  public async trackFunctionClicks() {
    const data = await this.fetchAdditionDeletion();
    this.saveSession(data, {
      sentence_template: this.getSelectedModule == 'sentence-templates-module' ? 1 : 0,
      text_generation: this.getSelectedModule == 'ai-text-generation-module' ? 1 : 0,
      grammar_check: this.getSelectedModule == 'grammar-check-module' ? 1 : 0,
      transitions: this.getSelectedModule == 'transition-module' ? 1 : 0,
      sentence_length: this.getSelectedModule == 'sentence-length-module' ? 1 : 0,
      paragraph_length: this.getSelectedModule == 'paragraph-length-module' ? 1 : 0,
      plagiarism_check: this.getSelectedModule == 'plagiarism-checker-module' ? 1 : 0,
      paraphraser: this.getSelectedModule == 'paraphraser-module' ? 1 : 0,
      summarizer: this.getSelectedModule == 'summarizer-module' ? 1 : 0,
      ai_paper_drafter: this.getSelectedModule == 'draft-genie-module' ? 1 : 0,
      bibliography_search: this.getSelectedModule == 'reference-search-module' ? 1 : 0,
      peer_review: this.getSelectedModule == 'reviewer-module' ? 1 : 0,
      cover_letter: this.getSelectedModule == 'cover-letter-module' ? 1 : 0,
      journal_selector: this.getSelectedModule == 'journal-selector-module' ? 1 : 0,
    });
  }

  public saveSession(data: any, functions: any = {}) {
    trackSession({
      ...data,
      version: 'en',
      text_id: this.document.id,
      app: 'Web',
      sentence_template: 0,
      text_generation: 0,
      grammar_check: 0,
      transitions: 0,
      sentence_length: 0,
      paragraph_length: 0,
      plagiarism_check: 0,
      paraphraser: 0,
      summarizer: 0,
      ai_paper_drafter: 0,
      bibliography_search: 0,
      peer_review: 0,
      cover_letter: 0,
      journal_selector: 0,
      ...functions
    })
      .then((res: any) => {
        return;
      })
      .catch((err) => { });
  }

  public idleInstance: any = null;

  public initTracker() {
    this.idleInstance = new IdleJs({
      idle: 180000,
      startAtIdle: true,
      keepTracking: true,
      events: ['keydown', 'mousedown', 'touchstart'],
      onIdle: () => {
        this.whenIdle();
      },
      onActive: () => {
        this.whenActive();
      }
    });
    this.idleInstance.start();
  }

  beforeUnmount() {
    this.idleInstance.stop();
  }

  @Watch('document.data')
  onDocumentDataChange() {
    this.query = this.document.data;
  }

  @Watch('payloadStore.payload.tour')
  public onTourLoaded() {
    if (this.tour?.isActive()) {
      this.currentStep = this.payloadStore.payload?.tour?.currentStep;
    } else {
      this.currentStep = '';
    }
  }

  public grammarCheckModel: any = '';
  public observer: any;

  async mounted() {
    this.payloadStore.change({ displayedFeatured: false, selectedModule: '' });
    await this.getDocument();
    this.grammarCheckModel = this.$route.query.model || '';
    this.query = this.document.data;
    this.selectedModule = this.payloadStore.payload.selectedModule;
    this.payloadStore.change({ functionError: false });
    this.payloadStore.change({ tour: {} });
    this.initTracker();
    //Grammar check observer
    const observer = new MutationObserver((mutationsList) => {
      mutationsList.forEach((mutation) => {
        if (mutation.type === 'childList') {
          this.checkIsLoadedGrammarChecker();
        }
      });
    });
    const targetNode = document.querySelector('.ql-container');
    if (targetNode) {
      const config = { childList: true, subtree: true };
      observer.observe(targetNode, config);
      this.checkIsLoadedGrammarChecker();
    } else {
      console.error('Parent component not found.');
    }
  }

  beforeDestroy() {
    this.observer.disconnect();
    document.removeEventListener('click', this.handleClickOutside);
  }

  public checkIsLoadedGrammarChecker() {
    
  }

  public handleClickOutside(event: any) {
    if (
      !event?.target?.parentElement?.className?.includes('ql-editor') &&
      !event?.target?.parentElement?.className?.includes('v-field__field')
    ) {
      if (this.getSelectedRange?.length >= 2) {
        if (
          this.getSelectedRange[1] &&
          this.getSelectedRange[1] > 0 &&
          this.getSelectedRange[1] > this.getSelectedRange[0]
        ) {
          this.quill.setSelection(this.getSelectedRange[0], this.getSelectedRange[1]);
        }
      }
    }
    // const nativeRange = this.quill.getNativeRange();
    // if (nativeRange) {
    //   console.log(nativeRange, 'ssc');
    // }
  }
  @Watch('payloadStore.payload.selectedModule')
  public onSelectedModuleChange() {
    this.payloadStore.change({ functionError: false });
    this.payloadStore.change({ loadingBrainResponse: false });
    this.payloadStore.change({ loadingResponse: false });
    this.summarizedText = '';
    this.paraphrasedText = '';
    this.selectedRange = [0, 0, 0];
    this.quill.removeFormat(0, this.quill.getText().length);
    this.selectedModule = this.payloadStore.payload.selectedModule;
    if (this.selectedModule === 'sentence-length-module') {
      this.handleToggleSentenceLength();
    } else if (this.selectedModule === 'paragraph-length-module') {
      this.handleToggleParagraphLength();
    } else if (this.selectedModule === 'transition-module') {
      this.handleToggleTransition();
    } else if (this.selectedModule === 'plagiarism-checker-module') {
      this.handleTogglePlagiarism();
    } else if (this.selectedModule === 'grammar-check-module') {
      this.handleToggleGrammarCheck();
    } else if (this.selectedModule === 'sentence-templates-module') {
      this.handleToggleSentenceTemplate();
    }
  }

  get getSelectedModule() {
    return this.selectedModule;
  }

  //Editor
  public query: any = '';
  public getModuleDrawerLoading: boolean = false;
  public editorOptions = {
    debug: 'info',
    readOnly: false,
    theme: '',
    modules: {
      languageTool: {
        server: 'https://languagetool.writewise.io',
        language: 'en-US',
        disableNativeSpellcheck: true,
        apiOptions: {
          level: 'default'
        },
        handleUndefinedMatch: true
      },
      toolbar: toolbarOptions,
      clipboard: {
        matchVisual: false
        // onPaste: (e: ClipboardEvent) => {
        //   e.preventDefault();
        //   const text = e.clipboardData?.getData('text/plain') || '';
        //   if (!text) return;
        //   this.handleInsertSentence(text, false); // false to skip formatting
        // }
      }
    },
    bounds: '#quillContainer',
    scrollingContainer: '#quillContainer',
    placeholder: 'Start writing here!'
  };

  public theEditor: any;
  public quill: any;

  get getSelectedRange() {
    return this.selectedRange;
  }

  private sentenceLengthFeedbackData: any = [];
  private plagiarismFeedbackData: any = [];
  private paragraphLengthFeedbackData: any = [];
  private transitionFeedbackData: any = [];
  private sentenceTemplateFeedbackData: any = [];

  get getSentenceLengthFeedbackData() {
    return this.sentenceLengthFeedbackData;
  }

  get getSentenceTemplateFeedbackData() {
    return this.sentenceTemplateFeedbackData;
  }

  get getParagraphLengthFeedbackData() {
    return this.paragraphLengthFeedbackData;
  }

  get getTransitionFeedbackData() {
    return this.transitionFeedbackData;
  }

  get getPlagiarismFeedbackData() {
    return this.plagiarismFeedbackData;
  }

  get getTotalPlagiarism() {
    return this.totalPlagiarism;
  }

  public selectCardSentence(cardId: number, range: [number, number]) {
    this.quill.setSelection(range[0], range[1]);
  }

  private highlightPlagiarizedSentence(cardId: number, range: [number, number]) {
    this.quill.removeFormat(0, this.quill.getLength());
    setTimeout(() => {
      this.quill.formatText(range[0], range[1], { background: 'red' });
      setTimeout(() => {
        const bounds = this.quill.getBounds(range[0], range[1]);
        this.quill.container.scrollIntoView();
      }, 500);
    }, 1000);

    // this.highlightCard(cardId);
  }

  private selectWord(range: [number, number]) {
    this.quill.setSelection(range[0], range[1]);
  }

  private handleToggleSentenceLength() {
    this.sentenceLengthFeedbackData = [];
    if (this.getSelectedModule === 'sentence-length-module') {
      this.payloadStore.change({ loadingBrainResponse: true });
      sentenceLength({ userId: Number(`${this.payloadStore.payload.user.pk}`), texto: this.quill.getText(), mm: 'abst' })
        .then((res: any) => {
          this.processSentenceLength(res?.data);
          this.payloadStore.change({ loadingBrainResponse: false });
        })
        .catch((err) => {
          Message.error('Could not analyze sentence length. Please try again.');
          this.payloadStore.change({ functionError: true });
          this.payloadStore.change({ loadingBrainResponse: false });
        });
      return;
    } else {
      this.quill.removeFormat(0, this.quill.getLength());
      console.log(this.quill.getText());
      return;
    }
  }

  private handleTogglePlagiarism() {
    this.plagiarismFeedbackData = [];
    if (this.getSelectedModule === 'plagiarism-checker-module') {
      if (this.quill.getLength() > 10000)
        return Message.error('Text must be less than 10,000 characters.');
      this.payloadStore.change({ loadingBrainResponse: true });
      plagiarismChecker({
        text: this.quill.getText(),
        userId: Number(`${this.payloadStore.payload.user.pk}`)
      })
        .then((res: any) => {
          this.processPlagiarism(res);
          this.payloadStore.change({ loadingBrainResponse: false });
        })
        .catch((err) => {
          Message.error(
            err?.response?.message || 'Could not run the plagiarism function. Please try again.'
          );
          this.payloadStore.change({ functionError: true });
          this.payloadStore.change({ loadingBrainResponse: false });
        });
      return;
    } else {
      this.quill.removeFormat(0, this.quill.getLength());
      console.log(this.quill.getText());
      return;
    }
  }

  private handleToggleParagraphLength() {
    this.paragraphLengthFeedbackData = [];
    if (this.getSelectedModule === 'paragraph-length-module') {
      this.payloadStore.change({ loadingBrainResponse: true });
      paragraphLength({ texto: this.quill.getText(), mm: 'abst' })
        .then((res: any) => {
          this.processParagraphLength(res);
          this.payloadStore.change({ loadingBrainResponse: false });
        })
        .catch((err) => {
          Message.error('Could not analyze paragraph length. Please try again.');
          this.payloadStore.change({ functionError: true });
          this.payloadStore.change({ loadingBrainResponse: false });
        });
      return;
    } else {
      this.quill.removeFormat(0, this.quill.getLength());
      return;
    }
  }

  private handleToggleTransition() {
    if (!this.silent) this.transitionFeedbackData = [];
    if (this.getSelectedModule === 'transition-module') {
      if (!this.silent) this.payloadStore.change({ loadingBrainResponse: true });
      transition({ texto: this.quill.getText() })
        .then((res: any) => {
          this.processTransition(res);
          // this.payloadStore.change({ loadingBrainResponse: false });
        })
        .catch((err) => {
          Message.error('Could not run the transition function. Please try again.');
          this.payloadStore.change({ functionError: true });
          this.payloadStore.change({ loadingBrainResponse: false });
        });
      return;
    } else {
      this.quill.removeFormat(0, this.quill.getLength());
      return;
    }
  }

  private async handleToggleGrammarCheck() {
    try {
      if (this.getSelectedModule === 'grammar-check-module') {
        const quillContainer: any = document.querySelector('#quillContainer');
        if (quillContainer) quillContainer.classList.remove('disable-languageTool');
        this.quill.focus();
      } else {
        if (!document.querySelector('.disable-languageTool')) {
          const quillContainer: any = document.querySelector('#quillContainer');
          if (quillContainer) quillContainer.classList.add('disable-languageTool');
          const ltPopup: any = document.querySelector('quill-lt-popup');
          if (ltPopup) {
            ltPopup.parentNode.removeChild(ltPopup);
          }
        }
      }
    } catch (error) {
      console.error('Error in handleToggleGrammarCheck:', error);
      Message.error('Error initializing grammar check. Please try again.');
    }
  }

  private handleToggleSentenceTemplate() {
    this.sentenceTemplateFeedbackData = [];
    if (this.getSelectedModule === 'sentence-templates-module') {
      this.payloadStore.change({ loadingBrainResponse: true });
      sentenceTemplates()
        .then((res: any) => {
          const sentenceTemplateFeedbackData = [];
          let id = 1;
          for (const key of res) {
            sentenceTemplateFeedbackData.push({
              value: id,
              title: key.category,
              payload: key.examples
            });
            id++;
          }
          this.sentenceTemplateFeedbackData = sentenceTemplateFeedbackData;
          this.payloadStore.change({ loadingBrainResponse: false });
        })
        .catch((err) => {
          Message.error('Could not fetch the sentence templates. Please reload your browser.');
          this.payloadStore.change({ functionError: true });
          this.payloadStore.change({ loadingBrainResponse: false });
        });
      return;
    } else {
      this.quill.removeFormat(0, this.quill.getLength());
      return;
    }
  }

  public summarizedText: string = '';
  public async handleSummarizerModuleSelection(): Promise<any> {
    // this.summarizedText = '';
    if (this.getSelectedModule === 'summarizer-module') {
      if (this.selectedText.length > 8000)
        return Message.error(
          `Summarizer limit exceeded. Please ensure the selected text is within 8000 characters.
          Please separate the text into smaller sections, and run the analysis multiple times.`,
          6000
        );
      this.payloadStore.change({ loadingBrainResponse: true });
      v2SummarizeText({
        userId: Number(`${this.payloadStore.payload.user.pk}`),
        text: this.selectedText,
        language: 'english',
        version: 'en', token: this.payloadStore.payload.token
      })
        .then((res: any) => {
          let content = res?.data || '';
          this.summarizedText = content;
          this.payloadStore.change({ loadingBrainResponse: false });
          Message.success('Text summarized');
          // if (res.data) {
          //   if (res.data.Error) {
          //     Message.error(res.data.Error);
          //     return;
          //   } else {
          //     Message.success('Text summarized');
          //     this.summarizedText = res.data.summary;
          //     console.log(this.summarizedText);
          //     return;
          //   }
          // } else {
          //   Message.error('Could not summarize selected text');
          // }
        })
        .catch((err) => {
          Message.error('Could not summarize selected text');
          this.payloadStore.change({ loadingBrainResponse: false });
        });
      return;
    } else {
      this.quill.removeFormat(0, this.quill.getLength());
      return;
    }
  }

  public paraphrasedText: string = '';
  public async handleParaphraserModuleSelection(): Promise<any> {
    if (this.getSelectedModule === 'paraphraser-module') {
      if (this.selectedText.length > 8000)
        return Message.error(
          `Paraphraser limit exceeded. Please ensure the selected text is within 8000 characters.
          Please separate the text into smaller sections, and run the analysis multiple times.`,
          6000
        );
      this.payloadStore.change({ loadingBrainResponse: true });
      v2ParaphraseText({
        userId: Number(`${this.payloadStore.payload.user.pk}`),
        text: this.selectedText,
        language: 'english',
        version: 'en',
        token: this.payloadStore.payload.token

      })
        .then((res: any) => {
          let content = res?.data || '';
          // for (const paraphrase of res.data) {
          //   content = `${content}${paraphrase[0]} `;
          // }
          this.paraphrasedText = content;
          this.payloadStore.change({ loadingBrainResponse: false });
          Message.success('Text Paraphrased');
        })
        .catch((err) => {
          Message.error('Could not paraphrase selected text');
          this.payloadStore.change({ loadingBrainResponse: false });
        });
      return;
    } else {
      this.quill.removeFormat(0, this.quill.getLength());
      return;
    }
  }

  public tryAgain() {
    this.summarizedText = '';
    this.paraphrasedText = '';
    this.selectedText = '';
    this.selectedRange = [0, 0, 0];
  }

  get isGrammarCheck() {
    return this.getSelectedModule === 'grammar-check-module';
  }

  @Watch('isGrammarCheck')
  private onIsGrammarCheck(val: boolean) {
    if (!val) {
      if (!document.querySelector('.disable-languageTool')) {
        const quillContainer: any = document.querySelector('#quillContainer');
        quillContainer.classList.add('disable-languageTool');
        const ltPopup: any = document.querySelector('quill-lt-popup');
        if (ltPopup) {
          ltPopup.parentNode.removeChild(ltPopup);
        }
      }
    }
  }

  private processSentenceLength(payload: any) {
    let goldStandardAvg = Math.round(payload.gold_standard.num_words_sent.mean);
    let goldStandardStd = Math.round(payload.gold_standard.num_words_sent.std);
    let sentenceId = 0;
    this.sentenceLengthFeedbackData = [];
    payload.text?.forEach((sentence: any) => {
      sentenceId += 1;
      const capturedSentenceId = sentenceId;
      let sentence_length = sentence.end - sentence.start;
      let errorType;
      if (sentence.words >= 18 && sentence.words <= 21) {
        errorType = 'fair';
        this.quill.formatText(
          sentence.start,
          sentence_length,
          {
            'ww-sentence': {
              type: errorType,
              id: sentenceId,
              ondblclick: (thiz: any) => {
                this.highlightCard(capturedSentenceId);
                this.quill.setSelection(sentence.start, sentence_length);
              }
            }
          },
          errorType
        );
      } else if (sentence.words >= 22 && sentence.words <= 27) {
        errorType = 'average';
        this.quill.formatText(
          sentence.start,
          sentence_length,
          {
            'ww-sentence': {
              type: errorType,
              id: sentenceId,
              ondblclick: (thiz: any) => {
                this.highlightCard(capturedSentenceId);
                this.quill.setSelection(sentence.start, sentence_length);
              }
            }
          },
          errorType
        );
      } else if (sentence.words >= 28) {
        errorType = 'bad';
        this.quill.formatText(
          sentence.start,
          sentence_length,
          {
            'ww-sentence': {
              type: errorType,
              id: sentenceId,
              ondblclick: (thiz: any) => {
                this.highlightCard(capturedSentenceId);
                this.quill.setSelection(sentence.start, sentence_length);
              }
            }
          },
          errorType
        );
      }
      if (errorType) {
        this.sentenceLengthFeedbackData.push({
          id: sentenceId,
          words: sentence.words,
          type: errorType,
          goldStandardAvg: goldStandardAvg,
          goldStandardStd: goldStandardStd,
          selectionRange: [sentence.start, sentence_length]
        });
      }
    });
  }

  private processParagraphLength(payload: any) {
    let goldStandardAvg = Math.round(payload[2]['abst'][0]);
    let goldStandardStd = Math.round(payload[2]['abst'][1]);
    let sentenceId = 0;
    this.paragraphLengthFeedbackData = [];
    payload[0]?.forEach((sentence: any) => {
      sentenceId += 1;
      const capturedSentenceId = sentenceId;
      let sentence_length = sentence.end - sentence.start;
      let errorType;
      if (sentence.paragraph_length < 3) {
        errorType = 'fair';
        this.quill.formatText(
          sentence.start,
          sentence_length,
          {
            'ww-sentence': {
              type: errorType,
              id: sentenceId,
              ondblclick: (thiz: any) => {
                this.highlightCard(capturedSentenceId);
                this.quill.setSelection(sentence.start, sentence_length);
              }
            }
          },
          errorType
        );
      } else if (sentence.paragraph_length >= 6 && sentence.paragraph_length <= 10) {
        errorType = 'average';
        this.quill.formatText(
          sentence.start,
          sentence_length,
          {
            'ww-sentence': {
              type: errorType,
              id: sentenceId,
              ondblclick: (thiz: any) => {
                this.highlightCard(capturedSentenceId);
                this.quill.setSelection(sentence.start, sentence_length);
              }
            }
          },
          errorType
        );
      } else if (sentence.paragraph_length >= 11) {
        errorType = 'bad';
        this.quill.formatText(
          sentence.start,
          sentence_length,
          {
            'ww-sentence': {
              type: errorType,
              id: sentenceId,
              ondblclick: (thiz: any) => {
                this.highlightCard(capturedSentenceId);
                this.quill.setSelection(sentence.start, sentence_length);
              }
            }
          },
          errorType
        );
      }
      if (errorType) {
        this.paragraphLengthFeedbackData.push({
          id: sentenceId,
          paragraphs: sentence.paragraph_length,
          category: sentence.category,
          type: errorType,
          goldStandardAvg: goldStandardAvg,
          goldStandardStd: goldStandardStd,
          selectionRange: [sentence.start, sentence_length]
        });
      }
    });
  }

  public transitionSuggestions: any = [];
  private async processTransition(payload: any) {
    let sentenceTransitId = 0;
    let sentenceNonTransitId = 50000000;
    if (!this.silent) this.transitionFeedbackData = [];
    this.transitionSuggestions = await this.fetchTransitionSuggestions();
    if (!this.silent) this.payloadStore.change({ loadingBrainResponse: true });
    const not_transit = payload[0]['not_transit'];
    const transit = payload[0]['transit'];
    not_transit.forEach((sentence: any) => {
      sentenceTransitId += 1;
      const capturedSentenceId = sentenceTransitId;
      let sentence_length = sentence[1] - sentence[0];
      this.quill.formatText(
        sentence[0],
        sentence_length,
        {
          'ww-sentence': {
            type: 'non-transit',
            id: sentenceTransitId,
            ondblclick: (thiz: any) => {
              this.highlightCard(capturedSentenceId);
              this.quill.setSelection(sentence[0], sentence_length);
            }
          }
        },
        'non-transit'
      );
      this.transitionFeedbackData.push({
        id: capturedSentenceId,
        category: 'not_transit',
        type: 'error',
        selectionRange: [sentence[0], sentence_length]
      });
    });

    transit.forEach((sentence: any) => {
      sentenceNonTransitId += 1;
      let sentence_length = sentence[1] - sentence[0];
      this.quill.formatText(
        sentence[0],
        sentence_length,
        {
          'ww-sentence': {
            type: 'step-success',
            id: '',
            ondblclick: (thiz: any) => {
              this.quill.setSelection(sentence[0], sentence_length);
            }
          }
        },
        'success'
      );
    });
    this.payloadStore.change({ loadingBrainResponse: false });
  }

  public fetchTransitionSuggestions() {
    const thiz = this;
    return new Promise(function (resolve, reject) {
      if (!thiz.silent) thiz.payloadStore.change({ loadingBrainResponse: true });
      transitConnectors()
        .then((res: any) => {
          const data = [];
          let id = 1;
          for (const key in res) {
            data.push({ id, title: key, submenus: res[key] });
            id++;
          }
          thiz.payloadStore.change({ loadingBrainResponse: false });
          resolve(data);
        })
        .catch((err) => {
          thiz.payloadStore.change({ loadingBrainResponse: false });
        });
    });
  }

  public totalPlagiarism = 0;
  private processPlagiarism(payload: any) {
    let sentenceId = 0;
    this.totalPlagiarism = payload.data.percentPlagiarism;
    this.plagiarismFeedbackData = [];
    payload.data?.sources?.forEach((sentence: any) => {
      sentence.matches?.forEach((match: any) => {
        sentenceId += 1;
        const capturedSentenceId = sentenceId;
        let sentence_length = match.inputEnd - match.inputStart;
        if (this.getSelectedModule === 'plagiarism-checker-module') {
          this.quill.formatText(
            match.inputStart,
            sentence_length,
            {
              'ww-sentence': {
                type: 'bad',
                id: sentenceId,
                ondblclick: (thiz: any) => {
                  this.highlightCard(capturedSentenceId);
                  this.quill.setSelection(match.inputStart, sentence_length);
                }
              }
            },
            'bad'
          );
          this.plagiarismFeedbackData.push({
            id: sentenceId,
            title: sentence.title,
            url: sentence.url,
            selectionRange: [match.inputStart, sentence_length]
          });
        }
      });
    });
  }

  // @Watch('selectedRange')
  // onSelectedRange() {
  //   if (this.selectedRange?.length > 2) {
  //     if (this.selectedRange[0] && this.selectedRange[1]) {
  //       this.quill.setSelection(this.selectedRange[0], this.selectedRange[1]);
  //     }
  //   }
  // }

  private selectedRange: [number, number, number] = [1, 0, 1];
  public handleSelectionChange({ range }: any, oldRange: any, source: string) {
    if (range != null && !this.loadingBrainResponse) {
      this.selectedRange = [range.index, range.length, range.index + range.length];
      this.selectedText = this.quill.getText(range.index, range.length) || '';
    }
  }

  public quillLoaded: boolean = false;
  private debounceTimeout: any = null;
  public handleTextChange() {
    if (!this.quillLoaded) return;
    if (this.debounceTimeout) {
      window.clearTimeout(this.debounceTimeout);
    }

    $('#txtsave').html('Saving ...');

    this.debounceTimeout = setTimeout(async () => {
      await this.queueSave();
    }, 5000);
  }

  private async queueSave() {
    const currentText = this.quill.getText().trim();
    const initialText = await this.payloadStore.getInitialText();
    // Don't queue if text hasn't changed
    if (currentText === initialText) {
      $('#txtsave').html('Saved');
      return;
    }

    this.pendingSave = currentText;

    // If already saving, just update pendingSave and return
    if (this.saveQueue) {
      return;
    }

    const processSaveQueue = async () => {
      this.saveQueue = true;

      try {
        while (this.pendingSave !== null) {
          const textToSave = this.pendingSave;
          this.pendingSave = null;

          // Set timeout to prevent infinite loops
          const saveTimeout = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Save timeout')), 30000);
          });

          try {
            await Promise.race([
              updateDocument({
                id: this.payloadStore.payload.document.id,
                title: this.document.title,
                data: textToSave
              }),
              saveTimeout
            ]).then(async (res: any) => {
              await this.payloadStore.updateInitialText(res.data);
              $('#txtsave').html('Saved');
            });
          } catch (error) {
            console.error('Save error:', error);
            Message.error('Save failed - will retry');
            if (this.pendingSave === null) {
              this.pendingSave = textToSave;
            }
            await new Promise(resolve => setTimeout(resolve, 5000)); // Wait before retry
          }
        }
      } finally {
        this.saveQueue = false;
      }
    };

    processSaveQueue();
  }

  public handleInsertSentence(sentence: string, format: boolean = true) {
    if (!this.getSelectedRange?.length) this.selectedRange = [0, 0, 0];

    // Use chunked insertion for large text
    if (sentence.length > 10000) {
      this.insertLargeText(sentence + ' ');
    } else {
      // Use existing logic for smaller text
      this.quill.deleteText(this.getSelectedRange[0], this.getSelectedRange[1]);
      this.quill.insertText(this.getSelectedRange[0], sentence + ' ', {
        italic: false
      });
      if (format) {
        setTimeout(() => {
          this.quill.formatText(
            this.getSelectedRange[0],
            sentence.length + 1,
            {
              'ww-sentence': {
                type: 'insert',
                id: 10
              }
            },
            'insert'
          );
          setTimeout(() => {
            this.quill.removeFormat(this.getSelectedRange[0], sentence.length + 1);
          }, 4000);
        }, 800);
      }
      this.saveSessionOnUpdate(); 
    }
  }

  public handleInsertWords(word: string, format: boolean = true) {
    if (!this.getSelectedRange?.length) this.selectedRange = [0, 0, 0];
    // this.quill.deleteText(this.getSelectedRange[0], this.getSelectedRange[1]);
    this.quill.insertText(this.getSelectedRange[0], word, {
      italic: false
    });
    this.selectedRange = [
      this.getSelectedRange[0] + word.length,
      word.length,
      this.getSelectedRange[0] + word.length + word.length
    ];
    // if (format) {
    //   setTimeout(() => {
    //     this.quill.formatText(
    //       this.getSelectedRange[0],
    //       sentence.length + 1,
    //       {
    //         'ww-sentence': {
    //           type: 'insert',
    //           id: 10
    //         }
    //       },
    //       'insert'
    //     );
    //     setTimeout(() => {
    //       console.log('dddsds', this.getSelectedRange[0], sentence.length + 1);
    //       this.quill.removeFormat(this.getSelectedRange[0], sentence.length + 1);
    //       // this.quill.removeFormat(0, this.quill.getLength());
    //     }, 4000);
    //   }, 800);
    // }
  }

  public silent = false;
  public handleInsertTransitionWord(sentence: string, format: boolean = true) {
    if (!this.getSelectedRange?.length) this.selectedRange = [0, 0, 0];
    this.quill.insertText(this.getSelectedRange[0], sentence + ' ', {
      italic: false
    });

    // this.silent = true;
    // this.onSelectedModuleChange();
    this.quill.setSelection(0, 0);
  }

  public isHighlightedCard(myCardId: number) {
    return this.getHighlightedCard == myCardId;
  }

  public highlightedCard: number = -1;

  get getHighlightedCard() {
    return this.highlightedCard;
  }

  private highlightCard(cardId: number) {
    this.highlightedCard = cardId;
    console.log('hl: ' + this.getHighlightedCard);
  }

  get getSelectedText() {
    return this.selectedText;
  }

  get getSummarizedText() {
    return this.summarizedText;
  }

  get getParaphrasedText() {
    return this.paraphrasedText;
  }

  get getTransitionSuggestions() {
    return this.transitionSuggestions;
  }

  get isRefresh() {
    return (
      this.getSelectedModule == 'transition-module' ||
      this.getSelectedModule == 'sentence-length-module' ||
      this.getSelectedModule == 'paragraph-length-module' ||
      this.getSelectedModule == 'plagiarism-checker-module'
    );
  }

  public insertLargeText(text: string) {
    try {
      // Show loading state
      this.payloadStore.change({ loadingBrainResponse: true });
      // Clean text synchronously
      const cleanText = text
        .replace(/\[(.*?)\]\((.*?)\)/g, '$1')
        .replace(/[*_~`#]/g, '')
        .replace(/\n{3,}/g, '\n\n')
        .trim();

      // Get starting position and disable editor
      const startIndex = this.quill.getSelection()?.index || 0;
      this.quill.enable(false);

      // Simple insert with basic formatting
      try {
        this.quill.deleteText(startIndex, this.getSelectedRange?.[1] || 0);
        this.quill.insertText(startIndex, cleanText + ' ', {
          bold: false,
          italic: false,
          underline: false
        });
      } catch (err) {
        console.error('Error inserting text:', err);
        Message.error('Error inserting text. Please try again.');
      }

      // Re-enable editor and clean up
      setTimeout(() => {
        this.quill.enable(true);
        this.payloadStore.change({ loadingBrainResponse: false });
      }, 1000);

    } catch (err) {
      console.error('Error in insertLargeText:', err);
      this.quill.enable(true);
      this.payloadStore.change({ loadingBrainResponse: false });
      Message.error('Error processing text. Please try again.');
    }
  }

  // Add near other class properties
  private saveQueue = false;
  private lastSavePromise: Promise<any> | null = null;
  private pendingSave: string | null = null;
}
</script>

<style>
.ww-sentence-error {
  margin: 1px;
  background-color: #ffc000;
}

.ww-sentence-error::selection {
  background-color: #ff8c00;
  opacity: 0.2;
  margin: 1px;
}

.ww-sentence-error-init {
  border-bottom: 2px solid #ffc000;
}

.ww-sentence-step-voice {
  background-color: #fce499;
  margin: 1px;
}

.ww-sentence-step-voice::selection {
  background-color: #ff8c00;
  opacity: 0.2;
  margin: 1px;
}

.ww-sentence-step-success {
  margin: 1px;
  color: #191818;
  background-color: #dbeafe;
}

.ww-sentence-highlight {
  /*display: block;*/
  text-decoration: underline;
  border: 1px black solid;
  margin: 1px;
}

.ww-sentence-insert {
  border-bottom: 2px solid #40a9ff;
}

.ww-sentence-non-transit {
  border-bottom: 2px solid #40a9ff;
}

.ww-sentence-non-transit::selection {
  background-color: #40a9ff;
  opacity: 0.2;
}

.ww-sentence-fair {
  border-bottom: 2px solid #ffc107;
}

.ww-sentence-fair::selection {
  background-color: #ffc107;
  opacity: 0.2;
}

.ww-sentence-bad {
  border-bottom: 2px solid #d50000 !important;
}

.ww-sentence-bad::selection {
  /*border-bottom: none !important;
  border-radius: 5px !important;
  background-color: rgba(213, 0, 0, 0.2);
  color: rgba(0, 0, 0, 1);*/
  background-color: #d50000;
  opacity: 0.2;
}

.ww-sentence-average {
  border-bottom: 2px solid #ff9e59;
}

.ww-sentence-average::selection {
  background-color: #ff9e59;
  opacity: 0.2;
}

*[class^='ww-keyconcept-highlight-'] {
  margin: 1px;
  background-color: rgba(59, 11, 59, 0.2);
}

.ww-similarity-warning {
  margin: 1px;
  background-color: #ffc000;
}

#quillContainer {
  /* max-height:89vh;
  overflow-y: auto;
  -ms-overflow-style: none; */
  margin-left: 15px;
  margin-top: -30px;
}

.cards-container-affix::-webkit-scrollbar,
#quillContainer::-webkit-scrollbar {
  display: none;
}

.ql-editor {
  font-size: 16px !important;
}

.ql-editor>* {
  font-size: 16px !important;
}

.ql-editor>p>span {
  font-size: 16px !important;
}

.component-message-wrap .message-content p {
  word-break: break-word;
}

.shepherd-footer {
  display: flex;
  justify-content: center;
  align-items: center;
}

.shepherd-button {
  background-color: #1890ff;
  color: #ffffff;
  border-radius: 10px;
}

.shepherd-element {
  border-radius: 16px;
}

quill-lt-popup {
  z-index: 1000;
}
</style>
